import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Avatar,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  FormHelperText,
  InputAdornment,
  Tooltip,
  Snackbar,
  Switch,
  FormControlLabel,
  Collapse
} from '@mui/material';
import {
  PhotoCamera as PhotoIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete,
  ContactPhone,
  Search as SearchIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { IFuncionario, FuncionarioCreationAttributes } from '../../types';
import funcionarioService from '../../services/funcionarioService';
import { toast } from 'react-toastify';
import ImageUploadCrop from '../common/ImageUploadCrop';
import EscritorioSelect from '../selects/EscritorioSelect';
import EstadosBrSelect from '../selects/EstadosBrSelect';
import { formatarCPFOnInput, formatarPhoneOnInput, formatarCepOnInput, formatarValor, formatarValorOnInput, buscarCep } from '../../utils/formatToView';

interface FuncionarioFormProps {
  funcionario?: IFuncionario | null;
  mode: 'create' | 'edit';
  onSuccess: () => void;
  onCancel: () => void;
}

const cargos = [
  'Administrativo',
  'Analista',
  'Aprendiz',
  'Desenvolvedor',
  'Estagiario',
  'Financeiro',
  'Gerente',
  'Marketing',
  'Operacional',
  'Recursos Humanos',
  'Suporte',
  'Tecnico',
  'Vendedor',
  'Outros'
];

const FuncionarioForm: React.FC<FuncionarioFormProps> = ({
  funcionario,
  mode,
  onSuccess,
  onCancel
}) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | undefined>(undefined);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState<string | null>(null);

  // Estado para valor formatado do salário
  const [salarioFormatado, setSalarioFormatado] = useState('0,00');

  // Estado para notificações
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [comissaoValorFormatado, setComissaoValorFormatado] = useState('0,00');

  // Valores iniciais
  const defaultValues: FuncionarioCreationAttributes = {
    nome: funcionario?.nome || '',
    cpf: funcionario?.cpf || '',
    rg: funcionario?.rg || '',
    data_nascimento: funcionario?.data_nascimento ? funcionario.data_nascimento.split('T')[0] : '',
    email: funcionario?.email || '',
    celular: funcionario?.celular || '',
    chave_pix: funcionario?.chave_pix || '',
    pix_banco: funcionario?.pix_banco || '',
    pix_titular: funcionario?.pix_titular || '',
    cargo: funcionario?.cargo || '',
    data_admissao: funcionario?.data_admissao ? funcionario.data_admissao.split('T')[0] : '',
    data_demissao: funcionario?.data_demissao ? funcionario.data_demissao.split('T')[0] : '',
    salario: funcionario?.salario || 0,
    status: funcionario?.status || 'ativo',
    tipo_contrato: funcionario?.tipo_contrato || 'CLT',
    endereco: funcionario?.endereco || '',
    numero: funcionario?.numero || '',
    complemento: funcionario?.complemento || '',
    bairro: funcionario?.bairro || '',
    cidade: funcionario?.cidade || '',
    estado: funcionario?.estado || '',
    cep: funcionario?.cep || '',
    anotacoes: funcionario?.anotacoes || '',
    foto: funcionario?.foto || '',
    escritorio_id: funcionario?.escritorio_id || undefined,
    comissao: funcionario?.comissao || false,
    comissao_tipo: funcionario?.comissao_tipo || null,
    comissao_calculo_tipo: funcionario?.comissao_calculo_tipo || null,
    comissao_valor: funcionario?.comissao_valor || null,
  };

  // Configurar preview da imagem e valores formatados
  useEffect(() => {
    if (funcionario?.foto) {
      setPreviewImage(funcionarioService.getAvatarUrl(funcionario.foto));
    }
    if (funcionario?.salario) {
      setSalarioFormatado(formatarValor(funcionario.salario));
    }
    if (funcionario?.comissao_valor) {
      setComissaoValorFormatado(formatarValor(funcionario.comissao_valor));
    }
  }, [funcionario]);

  // Função para converter string formatada para número
  const parsearMoeda = (valorFormatado: string): number => {
    // Remove todos os caracteres que não são números, vírgula ou ponto
    const numeroLimpo = valorFormatado.replace(/[^\d,]/g, '');
    // Substitui vírgula por ponto para conversão
    const numeroComPonto = numeroLimpo.replace(',', '.');
    const numero = parseFloat(numeroComPonto) || 0;
    return numero;
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm<FuncionarioCreationAttributes>({
    defaultValues
  });

  const cepWatch = watch('cep');
  const comissaoWatch = watch('comissao');
  const comissaoCalculoTipoWatch = watch('comissao_calculo_tipo');

  const handleBuscarCep = async () => {
    await buscarCep(cepWatch || '', setValue, setCepError, setCepLoading);
  };

  const onSubmit = async (values: FuncionarioCreationAttributes) => {
    try {
      setLoading(true);

      // Converter campos vazios em null
      const processedValues = Object.fromEntries(
        Object.entries(values).map(([key, value]) => [
          key,
          value === '' || value === undefined ? null : value
        ])
      ) as FuncionarioCreationAttributes;

      if (mode === 'create') {
        const response = await funcionarioService.createFuncionario(processedValues);
        if (response.success) {
          toast.success('Funcionário criado com sucesso');
          onSuccess();
        } else {
          toast.error(response.message || 'Erro ao criar funcionário');
        }
      } else {
        const response = await funcionarioService.updateFuncionario(funcionario!.id, processedValues);
        if (response.success) {
          toast.success('Funcionário atualizado com sucesso');
          onSuccess();
        } else {
          toast.error(response.message || 'Erro ao atualizar funcionário');
        }
      }
    } catch (error: any) {
      console.error('Erro ao salvar funcionário:', error);

      // Verificar se o erro tem dados estruturados do backend
      if (error.response?.data) {
        const errorData = error.response.data;

        // Se há erros específicos, exibir cada um
        if (errorData.errors && Array.isArray(errorData.errors)) {
          errorData.errors.forEach((errorMsg: string) => {
            toast.error(errorMsg);
          });
        } else if (errorData.message) {
          // Se há apenas uma mensagem de erro
          toast.error(errorData.message);
        } else {
          toast.error('Erro ao salvar funcionário');
        }
      } else if (error.message) {
        // Se é um erro JavaScript
        toast.error(error.message);
      } else {
        toast.error('Erro ao salvar funcionário');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handlers para upload de foto
  const handleUploadClick = () => {
    setUploadModalOpen(true);
  };

  const handleRemoveFoto = async () => {
    if (!funcionario) return;

    setUploading(true);

    try {
      // Como não existe removeFoto no serviço, vamos apenas limpar o campo
      setPreviewImage(undefined);
      setValue('foto', '');

      setNotification({
        open: true,
        message: 'Foto removida com sucesso!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Erro ao remover foto:', error);
      setNotification({
        open: true,
        message: 'Erro ao remover foto',
        severity: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleImageSave = async (croppedImageBlob: Blob, croppedImageUrl: string) => {
    if (!funcionario) return;

    setUploading(true);

    try {
      // Converter Blob para File
      const extension = croppedImageBlob.type === 'image/png' ? 'png' :
        croppedImageBlob.type === 'image/webp' ? 'webp' : 'jpg';
      const file = new File([croppedImageBlob], `foto_funcionario.${extension}`, {
        type: croppedImageBlob.type
      });

      const response = await funcionarioService.uploadFoto(funcionario.id, file);

      if (response.success && response.data) {
        setPreviewImage(funcionarioService.getAvatarUrl(response.data.foto));
        setValue('foto', response.data.foto);

        setNotification({
          open: true,
          message: 'Foto atualizada com sucesso!',
          severity: 'success',
        });
      } else {
        setNotification({
          open: true,
          message: response.message || 'Erro ao fazer upload da foto',
          severity: 'error',
        });
      }
    } catch (error: any) {
      console.error('Erro ao fazer upload da foto:', error);

      // Verificar se o erro tem dados estruturados do backend
      if (error.response?.data) {
        const errorData = error.response.data;

        // Se há erros específicos, exibir cada um
        if (errorData.errors && Array.isArray(errorData.errors)) {
          errorData.errors.forEach((errorMsg: string) => {
            setNotification({
              open: true,
              message: errorMsg,
              severity: 'error',
            });
          });
        } else if (errorData.message) {
          // Se há apenas uma mensagem de erro
          setNotification({
            open: true,
            message: errorData.message,
            severity: 'error',
          });
        } else {
          setNotification({
            open: true,
            message: 'Erro ao fazer upload da foto',
            severity: 'error',
          });
        }
      } else if (error.message) {
        // Se é um erro JavaScript
        setNotification({
          open: true,
          message: error.message,
          severity: 'error',
        });
      } else {
        setNotification({
          open: true,
          message: 'Erro ao fazer upload da foto',
          severity: 'error',
        });
      }
    } finally {
      setUploading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({
      open: false,
      message: '',
      severity: 'success',
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
      {/* Foto */}
      <Box display="flex" justifyContent="center" mb={3}>
        <Box position="relative">
          <Avatar
            src={previewImage}
            sx={{
              width: 120,
              height: 120,
              fontSize: '3rem',
              mx: 'auto',
              mb: 2,
              border: '2px solid white',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              borderRadius: '50%',
            }}
          />
          {funcionario && (
            <Tooltip title="Alterar Foto">
              <IconButton
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark'
                  }
                }}
                onClick={handleUploadClick}
                disabled={uploading}
              >
                {uploading ? <CircularProgress size={20} /> : <PhotoIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Informações Pessoais */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Informações Pessoais
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="nome"
            control={control}
            rules={{ required: 'Nome é obrigatório' }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Nome Completo *"
                error={!!errors.nome}
                helperText={errors.nome?.message}
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="cpf"
            control={control}
            rules={{ required: 'CPF é obrigatório' }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="CPF *"
                onChange={(e) => {
                  const formatted = formatarCPFOnInput(e.target.value);
                  field.onChange(formatted);
                }}
                error={!!errors.cpf}
                helperText={errors.cpf?.message}
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="rg"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="RG"
                error={!!errors.rg}
                helperText={errors.rg?.message}
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="data_nascimento"
            control={control}
            rules={{ required: 'Data de nascimento é obrigatória' }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Data de Nascimento *"
                type="date"
                error={!!errors.data_nascimento}
                helperText={errors.data_nascimento?.message}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: "1900-01-01", max: "2999-12-31" }}
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Email"
                type="email"
                error={!!errors.email}
                helperText={errors.email?.message}
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="celular"
            control={control}
            rules={{
              required: 'Celular é obrigatório',
              validate: (value) => {
                const digits = (value || '').replace(/\D/g, '');
                if (digits.length !== 11) {
                  return 'Celular deve ter 11 dígitos';
                }
                return true;
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Celular *"
                placeholder="(11) 99999-9999"
                onChange={(e) => {
                  const formatted = formatarPhoneOnInput(e.target.value, 11);
                  console.log("formatted", formatted);
                  field.onChange(formatted);
                }}
                InputProps={{
                  startAdornment: <ContactPhone sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                error={!!errors.celular}
                helperText={errors.celular?.message}
                size="small"
              />
            )}
          />
        </Grid>

        {/* Informações PIX */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Informações PIX
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name="chave_pix"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Chave PIX"
                error={!!errors.chave_pix}
                helperText={errors.chave_pix?.message}
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name="pix_banco"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Nome do Banco PIX"
                error={!!errors.pix_banco}
                helperText={errors.pix_banco?.message}
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name="pix_titular"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Titular PIX"
                error={!!errors.pix_titular}
                helperText={errors.pix_titular?.message}
                size="small"
              />
            )}
          />
        </Grid>

        {/* Informações Profissionais */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Informações Profissionais
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name="cargo"
            control={control}
            rules={{ required: 'Cargo é obrigatório' }}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.cargo} size="small">
                <InputLabel>Cargo *</InputLabel>
                <Select {...field} label="Cargo *">
                  <MenuItem value="">Selecione um cargo</MenuItem>
                  {cargos.map((cargo) => (
                    <MenuItem key={cargo} value={cargo}>
                      {cargo}
                    </MenuItem>
                  ))}
                </Select>
                {errors.cargo && (
                  <FormHelperText>{errors.cargo.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>


          <Controller
            name="salario"
            control={control}
            rules={{ required: 'Salário é obrigatório', min: { value: 0, message: 'Salário deve ser positivo' } }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Salário *"
                type="text"
                value={salarioFormatado}
                onChange={(e) => {
                  const valorFormatado = formatarValorOnInput(e.target.value);
                  const valorNumerico = parsearMoeda(valorFormatado);

                  setSalarioFormatado(valorFormatado);
                  field.onChange(valorNumerico);
                }}
                error={!!errors.salario}
                helperText={errors.salario?.message}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>
                }}
                placeholder="R$ 0,00"
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name="tipo_contrato"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.tipo_contrato} size="small">
                <InputLabel>Tipo de Contrato</InputLabel>
                <Select {...field} label="Tipo de Contrato">
                  <MenuItem value="CLT">CLT</MenuItem>
                  <MenuItem value="PJ">PJ</MenuItem>
                  <MenuItem value="estagiario">Estagiário</MenuItem>
                  <MenuItem value="temporario">Temporário</MenuItem>
                </Select>
                {errors.tipo_contrato && (
                  <FormHelperText>{errors.tipo_contrato.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.status} size="small">
                <InputLabel>Status</InputLabel>
                <Select {...field} label="Status">
                  <MenuItem value="ativo">Ativo</MenuItem>
                  <MenuItem value="inativo">Inativo</MenuItem>
                  <MenuItem value="demitido">Demitido</MenuItem>
                  <MenuItem value="aposentado">Aposentado</MenuItem>
                </Select>
                {errors.status && (
                  <FormHelperText>{errors.status.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name="data_admissao"
            control={control}
            rules={{ required: 'Data de admissão é obrigatória' }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Data de Admissão *"
                type="date"
                error={!!errors.data_admissao}
                helperText={errors.data_admissao?.message}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: "1900-01-01", max: "2999-12-31" }}
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name="data_demissao"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Data de Demissão"
                type="date"
                error={!!errors.data_demissao}
                helperText={errors.data_demissao?.message}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: "1900-01-01", max: "2999-12-31" }}
                size="small"
              />
            )}
          />
        </Grid>

        {/* Escritório */}
        <Grid item xs={12} md={4}>
          <Controller
            name="escritorio_id"
            control={control}
            render={({ field }) => (
              <EscritorioSelect
                value={field.value || null}
                onChange={(value) => field.onChange(value)}
                label="Escritório"
                placeholder="Selecione o escritório"
                error={!!errors.escritorio_id}
                helperText={errors.escritorio_id?.message || "Escritório onde o funcionário atua"}
                size="small"
                multiple={false}
                onlyActive={true}
                allowClear={true}
                fullWidth={true}
                required={true}
              />
            )}
          />
        </Grid>

        {/* Endereço */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Endereço
          </Typography>
        </Grid>
        <Grid item xs={12} md={3}>
          <Controller
            name="cep"
            control={control}
            rules={{ required: 'CEP é obrigatório' }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="CEP *"
                onChange={(e) => {
                  const formatted = formatarCepOnInput(e.target.value);
                  field.onChange(formatted);
                  setCepError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleBuscarCep();
                  }
                }}
                error={!!errors.cep || !!cepError}
                helperText={errors.cep?.message || cepError}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Buscar CEP">
                        <IconButton
                          onClick={handleBuscarCep}
                          disabled={cepLoading || !cepWatch || cepWatch.replace(/\D/g, '').length < 8}
                          size="small"
                          sx={{ p: 0.5 }}
                        >
                          {cepLoading ? (
                            <CircularProgress size={16} />
                          ) : (
                            <SearchIcon sx={{ fontSize: '1rem' }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="cidade"
            control={control}
            rules={{ required: 'Cidade é obrigatória' }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Cidade *"
                error={!!errors.cidade}
                helperText={errors.cidade?.message}
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <Controller
            name="estado"
            control={control}
            rules={{ required: 'Estado é obrigatório' }}
            render={({ field }) => (
              <EstadosBrSelect
                value={field.value || ''}
                onChange={field.onChange}
                label="Estado *"
                error={!!errors.estado}
                helperText={errors.estado?.message}
                required
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Controller
            name="bairro"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Bairro"
                error={!!errors.bairro}
                helperText={errors.bairro?.message}
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="endereco"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Endereço"
                error={!!errors.endereco}
                helperText={errors.endereco?.message}
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <Controller
            name="numero"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Número"
                error={!!errors.numero}
                helperText={errors.numero?.message}
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <Controller
            name="complemento"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Complemento"
                error={!!errors.complemento}
                helperText={errors.complemento?.message}
                size="small"
              />
            )}
          />
        </Grid>

        {/* Comissões */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
            Comissões
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="comissao"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={field.value || false}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      if (!e.target.checked) {
                        setValue('comissao_tipo', null);
                        setValue('comissao_calculo_tipo', null);
                        setValue('comissao_valor', null);
                        setComissaoValorFormatado('0,00');
                      }
                    }}
                    color="primary"
                  />
                }
                label="Habilitar Comissão para este Funcionário"
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Collapse in={comissaoWatch} timeout="auto" unmountOnExit>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Controller
                  name="comissao_tipo"
                  control={control}
                  rules={comissaoWatch ? { required: 'Tipo de comissão é obrigatório' } : {}}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.comissao_tipo} size="small">
                      <InputLabel>Tipo de Comissão *</InputLabel>
                      <Select {...field} value={field.value || ''} label="Tipo de Comissão *">
                        <MenuItem value="venda">Por Venda (contrato ativado)</MenuItem>
                        <MenuItem value="recebimento">Por Recebimento (fatura paga)</MenuItem>
                      </Select>
                      {errors.comissao_tipo && (
                        <FormHelperText>{errors.comissao_tipo.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="comissao_calculo_tipo"
                  control={control}
                  rules={comissaoWatch ? { required: 'Tipo de cálculo é obrigatório' } : {}}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.comissao_calculo_tipo} size="small">
                      <InputLabel>Tipo de Cálculo *</InputLabel>
                      <Select {...field} value={field.value || ''} label="Tipo de Cálculo *">
                        <MenuItem value="percentual">Percentual (%)</MenuItem>
                        <MenuItem value="valor_fixo">Valor Fixo (R$)</MenuItem>
                      </Select>
                      {errors.comissao_calculo_tipo && (
                        <FormHelperText>{errors.comissao_calculo_tipo.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="comissao_valor"
                  control={control}
                  rules={comissaoWatch ? { required: 'Valor da comissão é obrigatório', min: { value: 0.01, message: 'Valor deve ser maior que zero' } } : {}}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Valor da Comissão *"
                      type="text"
                      value={comissaoValorFormatado}
                      onChange={(e) => {
                        const valorFormatado = formatarValorOnInput(e.target.value);
                        const valorNumerico = parsearMoeda(valorFormatado);
                        setComissaoValorFormatado(valorFormatado);
                        field.onChange(valorNumerico);
                      }}
                      error={!!errors.comissao_valor}
                      helperText={errors.comissao_valor?.message || (comissaoCalculoTipoWatch === 'percentual' ? 'Ex: 10 = 10% sobre o valor' : 'Valor fixo por contrato/fatura')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {comissaoCalculoTipoWatch === 'percentual' ? '%' : 'R$'}
                          </InputAdornment>
                        )
                      }}
                      size="small"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Grid>

        {/* Anotações */}
        <Grid item xs={12}>
          <Controller
            name="anotacoes"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Anotações"
                multiline
                rows={4}
                error={!!errors.anotacoes}
                helperText={errors.anotacoes?.message}
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Botões */}
      <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
        <Button
          variant="outlined"
          onClick={onCancel}
          startIcon={<CancelIcon />}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={loading}
        >
          {loading ? 'Salvando...' : mode === 'create' ? 'Criar' : 'Atualizar'}
        </Button>
      </Box>

      {/* Modal de Upload de Foto */}
      <ImageUploadCrop
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onImageSave={handleImageSave}
        title="Upload de Foto do Funcionário"
        aspectRatio={1} // Quadrado
        maxWidth={400}
        maxHeight={400}
        quality={0.9}
        outputFormat="auto"
        circularCrop={true}
        minCropSize={100}
      />

      {/* Snackbar de Notificação */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FuncionarioForm; 